import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AppFormComponent } from '@shared/components/ui/list-components';
import { CreationButtons } from '@shared/components/ui/creation-buttons/creation-buttons';
import { ErrorService } from '@shared/services/error.service';
import { InputFieldComponent } from '@shared/components/form/input/input-field.component';
import { LabelComponent } from '@shared/components/form/label/label.component';
import { Modal } from '@shared/components/common/modal/modal';
import { SelectFieldComponent, SelectOption } from '@shared/components/form/select/select-field.component';
import { SnackbarService } from '@shared/services/snackbar.service';
import { forkJoin } from 'rxjs';

import { OpsColor, OpsPunto, StockVitrificadoEmbrion } from '../embriones.interface';
import { EmbrionesService } from '../embriones.service';
import { etiquetaPajuela } from '../ops-pajuela.util';

interface DialogData {
  stockId: number;
  fechaVit: string;
  pajuela: StockVitrificadoEmbrion;
}

/**
 * El OPS marca cada pajuela: un punto dibujado en un color, leído de bisel para
 * atrás. De esas dos marcas más la fecha de vitrificación sale la identificación
 * legible que el técnico usa para encontrarla en el rack.
 */
@Component({
  selector: 'app-editar-ops-pajuela',
  imports: [
    AppFormComponent,
    CreationButtons,
    InputFieldComponent,
    LabelComponent,
    Modal,
    ReactiveFormsModule,
    SelectFieldComponent,
  ],
  templateUrl: './editar-ops-pajuela.html',
})
export class EditarOpsPajuela implements OnInit {
  saveForm!: FormGroup;
  loading = false;
  loadingCombos = true;

  puntos: OpsPunto[] = [];
  colores: OpsColor[] = [];

  private fB = inject(FormBuilder);
  private embrionesService = inject(EmbrionesService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogRef = inject(MatDialogRef<EditarOpsPajuela>);
  private dialogData: DialogData = inject(MAT_DIALOG_DATA);

  get pajuela(): StockVitrificadoEmbrion {
    return this.dialogData.pajuela;
  }

  /**
   * Issue #19 (2026-09-15): la pajuela Direct Transfer no se marca con OPS, se
   * identifica con el ID que trae impreso. Se reconoce por tenerlo cargado: es
   * obligatorio al generarla desde el cultivo.
   */
  get esDirectTransfer(): boolean {
    return this.pajuela.identificador !== null;
  }

  get puntosOptions(): SelectOption[] {
    return this.puntos.map(punto => ({ value: punto.id, label: punto.codigo }));
  }

  get coloresOptions(): SelectOption[] {
    return this.colores.map(color => ({ value: color.id, label: color.nombre }));
  }

  /** Vista previa de la etiqueta mientras se eligen las marcas. */
  get etiquetaPrevia(): string | null {
    if (this.esDirectTransfer) {
      return this.saveForm?.get('identificador')?.value || null;
    }

    const puntoId = Number(this.saveForm?.get('param_ops_punto_id')?.value);
    const colorId = Number(this.saveForm?.get('param_ops_color_id')?.value);

    return etiquetaPajuela(
      this.dialogData.fechaVit,
      this.puntos.find(punto => punto.id === puntoId),
      this.colores.find(color => color.id === colorId),
    );
  }

  ngOnInit(): void {
    this.makeForm();
    this.getCombos();
  }

  private makeForm(): void {
    this.saveForm = this.fB.group({
      param_ops_punto_id: [this.pajuela.param_ops_punto_id ?? '', this.esDirectTransfer ? [] : Validators.required],
      param_ops_color_id: [this.pajuela.param_ops_color_id ?? '', this.esDirectTransfer ? [] : Validators.required],
      identificador: [this.pajuela.identificador ?? '', this.esDirectTransfer ? Validators.required : []],
    });
  }

  private getCombos(): void {
    forkJoin([
      this.embrionesService.getOpsPuntos(),
      this.embrionesService.getOpsColores(),
    ]).subscribe({
      next: ([puntosResponse, coloresResponse]) => {
        this.puntos = puntosResponse.data || [];
        this.colores = coloresResponse.data || [];
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    }).add(() => {
      this.loadingCombos = false;
    });
  }

  save(): void {
    if (this.saveForm.invalid) {
      return;
    }

    const raw = this.saveForm.getRawValue();

    this.loading = true;
    this.embrionesService.actualizarPajuela(this.dialogData.stockId, this.pajuela.id, {
      param_ops_punto_id: this.esDirectTransfer ? null : Number(raw.param_ops_punto_id),
      param_ops_color_id: this.esDirectTransfer ? null : Number(raw.param_ops_color_id),
      ...(this.esDirectTransfer ? { identificador: raw.identificador.trim() } : {}),
    }).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar(this.esDirectTransfer ? 'ID de la pajuela actualizado correctamente' : 'Marca OPS actualizada correctamente');
          this.dialogRef.close(true);
        }
      },
      error: (error) => {
        this.errorService.handleError(error);
      },
    }).add(() => {
      this.loading = false;
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }
}
